// Common utility functions for team-related operations

/**
 * Gets the initial letter from a team name for placeholder display
 * @param {Object} team - Team object with name properties
 * @returns {string} Single letter or '?' as fallback
 */
export function getTeamInitial(team) {
  const name = team.display_name || team.short_display_name || team.abbrev || team.name || team.school || '?';
  return name && name.split ? name.split(' ').at(-1)?.[0] || '?' : '?';
}

/**
 * Handles image loading errors by replacing the img with a placeholder div
 * @param {Event} event - The error event from the img element
 */
export function handleImageError(event) {
  // Fallback to placeholder if logo fails to load
  const img = event.target;
  const placeholder = document.createElement('div');
  placeholder.className = 'logo-placeholder';
  const teamName = img.alt || '?';
  placeholder.textContent = teamName && teamName.split ? teamName.split(' ').at(-1)?.[0] || '?' : '?';
  img.parentNode.replaceChild(placeholder, img);
}

/**
 * Gets a reliable logo URL for a team, prioritizing provided logos and falling back to CFBD CDN
 * @param {Object} team - Team object with logos or id
 * @returns {string|null} Safe HTTPS image URL or null
 */
export function getTeamLogoUrl(team) {
  if (!team) return null;
  if (team.logos && team.logos.length > 0 && team.logos[0]) {
    return team.logos[0].replace(/^http:\/\//, 'https://');
  }
  if (team.logo) {
    return team.logo.replace(/^http:\/\//, 'https://');
  }
  if (team.id) {
    return `https://cdn.collegefootballdata.com/logos/500/${team.id}.png`;
  }
  return null;
}

/**
 * Transforms CFBD team data to match frontend component expectations
 * @param {Object} team - Team object from CFBD API
 * @returns {Object} Transformed team object
 */
export function transformTeamData(team) {
  const logos = (team.logos || []).map(u => u.replace(/^http:\/\//, 'https://'));
  const logo = logos[0] || (team.id ? `https://cdn.collegefootballdata.com/logos/500/${team.id}.png` : null);
  return {
    id: team.id,
    display_name: team.school || team.name,
    short_display_name: team.abbreviation || team.name?.split(' ').last,
    abbrev: team.abbreviation,
    logo: logo,
    rank: 99, // CFBD doesn't provide rank in teams endpoint
    location: team.location,
    conference: team.conference,
    color: team.color,
    alternate_color: team.alternate_color,
    mascot: team.mascot,
    logos: logos.length > 0 ? logos : (logo ? [logo] : [])
  };
}

/**
 * Gets a safe team name for display purposes
 * @param {Object} team - Team object with name properties
 * @returns {string} Team name or fallback
 */
export function getTeamDisplayName(team) {
  return team.display_name || team.short_display_name || team.abbrev || team.name || team.school || 'Unknown Team';
}

/**
 * Gets a short team name for display purposes
 * @param {Object} team - Team object with name properties
 * @returns {string} Short team name or fallback
 */
export function getTeamShortName(team) {
  return team.short_display_name || team.abbrev || team.display_name?.split(' ').last || team.name?.split(' ').last || team.school?.split(' ').last || '?';
}